// arcola-attribution-capture.js — WP-side first-touch attribution capture.
//
// QMS-DOC-051zb §16.0.5. Reads the campaign tags (utm_source / utm_medium /
// utm_campaign) and the Google and Meta click ids (gclid / fbclid) from the
// landing URL and keeps them in a first-party cookie, `_arc_attribution`,
// scoped to .arcolatheatre.com so the GTM sGTM-ingest tag can read it back
// inside the Spektrix iframe at boxoffice.arcolatheatre.com and credit a later
// booking to the advert (route C, QMS-DOC-051zi §2.1a).
//
// **ADVERTISING CONSENT IS REQUIRED. This cookie is NOT strictly necessary.**
// It stores advertising click identifiers so that a booking can be credited to
// an advert, which is not something the visitor asked for, so PECR Regulation 6
// requires prior consent (ISMS-DOC-023 Appendix C.4 Finding 1, ruled
// 2026-10-14; QMS-DOC-051zm v1.7 `_arc_attribution` row: category Advertising,
// ABSENT under no_decision and reject_all). An earlier version of this file
// wrote the cookie whatever the visitor chose and called it strictly necessary;
// that was wrong.
//
// Behaviour:
//   - Consent is read from the self-hosted CMP's `cc_cookie` (vanilla-
//     cookieconsent v3, apex-scoped), categories array, the same source the
//     GTM `Advertising Consent Flag` variable reads (scripts/gtm/sgtm-consent-
//     flag.js). No `cc_cookie`, or no `advertising` in it, means NOT granted.
//   - Granted at page load: capture from the URL (first touch wins).
//   - Not granted at page load: write nothing, and DELETE any existing
//     `_arc_attribution` (a refuser or an undecided visitor holds none).
//   - Consent decided or changed on THIS page: the CMP dispatches
//     `cc:onFirstConsent` / `cc:onChange` / `cc:onConsent` on window. On each,
//     re-read the choice: granted -> capture from this page's landing URL, so
//     a visitor who lands from an advert and accepts on the first page keeps
//     the attribution; withdrawn -> delete the cookie.
//
// First-touch attribution: once values are stored, subsequent landings with
// different UTM params do NOT overwrite. The 30-day lifetime brackets a
// typical Arcola customer's deliberation window.
//
// Build: this is the SOURCE. `npm run build` (vite.attribution.config.js, lib
// IIFE) writes public/js/arcola-attribution-capture.js; commit both. Deploy:
// deploy-node-wp rsyncs infra/wp/themes/** into mana-wp; served at
// https://www.arcolatheatre.com/wp-content/themes/arcola-theatre-theme/public/js/arcola-attribution-capture.js
// Enqueued via the WP theme's Enqueue.php::publicScripts().
// Tests: app/src/lib/server/pipeline/marketing/__tests__/arcola-attribution-capture.test.ts
// (run the BUILT file, which is what visitors receive).

(function () {
    "use strict";

    var COOKIE_NAME = "_arc_attribution";
    var COOKIE_DOMAIN = ".arcolatheatre.com";
    var COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60; // 30 days
    var LOG_PREFIX = "[arcola-attribution]";
    var CONSENT_EVENTS = ["cc:onFirstConsent", "cc:onChange", "cc:onConsent"];

    // Param names captured from URL → stored in cookie. Cookie keys are
    // camelCase / no-prefix.
    var PARAM_MAP = [
        { url: "utm_source", key: "utmSource" },
        { url: "utm_medium", key: "utmMedium" },
        { url: "utm_campaign", key: "utmCampaign" },
        { url: "gclid", key: "gclid" },
        // fbclid: Meta's click id. The sgtm-ingest tag reconstructs _fbc from
        // this (fb.1.<capturedAt>.<fbclid>) for consented visitors.
        // QMS-DOC-051zi §2.1a step 5 / 037c §10.7.
        { url: "fbclid", key: "fbclid" }
    ];

    function readCookie(name) {
        var m = document.cookie.match(
            // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp -- name is a literal at every call site ("cc_cookie", COOKIE_NAME) - not input
            new RegExp("(?:^|; )" + name + "=([^;]*)")
        );
        if (!m) return null;
        try {
            return JSON.parse(decodeURIComponent(m[1]));
        } catch (e) {
            return null;
        }
    }

    // True only when the visitor's recorded choice includes advertising.
    // Fail closed: no cc_cookie, a malformed one, or any error -> false.
    function hasAdvertisingConsent() {
        try {
            var cc = readCookie("cc_cookie");
            return !!(
                cc &&
                Array.isArray(cc.categories) &&
                cc.categories.indexOf("advertising") > -1
            );
        } catch (e) {
            return false;
        }
    }

    function writeCookie(name, valueObj) {
        var encoded = encodeURIComponent(JSON.stringify(valueObj));
        document.cookie =
            name +
            "=" +
            encoded +
            "; Max-Age=" +
            COOKIE_MAX_AGE_SECONDS +
            "; Path=/; Domain=" +
            COOKIE_DOMAIN +
            "; SameSite=Lax; Secure";
    }

    function deleteCookie(name) {
        // The apex-scoped copy this file writes, and a host-only copy in case
        // one was ever written without a Domain attribute.
        document.cookie =
            name + "=; Max-Age=0; Path=/; Domain=" + COOKIE_DOMAIN + "; SameSite=Lax; Secure";
        document.cookie = name + "=; Max-Age=0; Path=/";
    }

    function hasAttributionCookie() {
        return new RegExp("(?:^|; )" + COOKIE_NAME + "=").test(document.cookie);
    }

    function readUrlParam(name) {
        try {
            return new URLSearchParams(window.location.search).get(name);
        } catch (e) {
            return null;
        }
    }

    function captureAttribution() {
        var existing = readCookie(COOKIE_NAME) || {};
        var captured = {};
        var anyFound = false;
        for (var i = 0; i < PARAM_MAP.length; i++) {
            var entry = PARAM_MAP[i];
            var fromUrl = readUrlParam(entry.url);
            if (fromUrl && String(fromUrl).length > 0) {
                captured[entry.key] = String(fromUrl).slice(0, 200);
                anyFound = true;
            }
        }
        if (!anyFound) {
            // No new UTM/click id on this URL — leave any existing cookie alone.
            return;
        }
        // First-touch attribution: only WRITE if the existing cookie has zero
        // stored params. Subsequent landings preserve the original attribution.
        for (var j = 0; j < PARAM_MAP.length; j++) {
            if (existing[PARAM_MAP[j].key]) {
                return;
            }
        }
        captured.capturedAt = new Date().toISOString();
        writeCookie(COOKIE_NAME, captured);
    }

    // Apply the visitor's CURRENT choice: capture when advertising is granted,
    // otherwise make sure no attribution cookie remains.
    function applyConsent() {
        try {
            if (hasAdvertisingConsent()) {
                captureAttribution();
            } else if (hasAttributionCookie()) {
                deleteCookie(COOKIE_NAME);
            }
        } catch (e) {
            console.error(LOG_PREFIX, "applyConsent threw", e);
        }
    }

    applyConsent();

    // A decision made on this page (first consent, or a later change in the
    // preferences panel). The CMP updates cc_cookie before it dispatches.
    for (var k = 0; k < CONSENT_EVENTS.length; k++) {
        try {
            window.addEventListener(CONSENT_EVENTS[k], applyConsent);
        } catch (e) {
            /* very old browser — page-load decision above still applies */
        }
    }
})();
